import type { DocNode } from '@atlaskit/adf-schema';
import { areNodesEqualIgnoreAttrs, isEmptyDocument } from '@atlaskit/editor-common/utils/document';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import type { StreamFlowPacer } from '@atlassian/editor-ai-common/api/stream-flow-pacer';
import { normaliseAIExperienceEvent } from '@atlassian/editor-ai-common/utils/normaliseEvents';
import type { EditorPluginAIActions } from '@atlassian/editor-plugin-ai';
import { UNSTABLE_configItems } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

import type { API, StreamingCommand } from '../aiStreamingOrchestratorPluginType';
import type { InitOptions, QueuedStreamCommand } from '../types/StreamCommandQueue';

import { DebugLogger } from './aifc-logging';
import {
	checkAndFireNoDocumentChangeAnalytics,
	createFireADFStreamingAnalyticsEvent,
	fireAttributeOnlyReplaceNodeAnalytics,
	fireSessionStartedAnalytics,
	fireToolCallCapturedAnalytics,
	fireCommandError,
} from './analytics';
import { findNodeByLocalIdAndApi } from './utils/find-node-by-local-id';
import { getPmDoc } from './utils/get-pm-doc';
import { isAStreamableCommand } from './utils/is-a-streamable-command';
import { makeDocumentValid } from './utils/make-document-valid';
import { mergeStreamCommands } from './utils/merge-stream-commands';
import { createTimeoutWrapper } from './utils/timeout-wrapper';
import { updateInsertAfterDoc } from './utils/update-insert-after-doc';

import { createStreamingCommands } from './index';

type EditorPluginAIEvent = Parameters<EditorPluginAIActions['dispatchAIEvent']>[0];

export class StreamCommandQueue {
	private queue: QueuedStreamCommand[] = [];
	private adfStreamer: ADFStreamer | undefined;
	private streamingCommands: ReturnType<typeof createStreamingCommands>;
	private currentCommand: StreamingCommand | undefined = undefined;
	private docBeforeStreaming: PMNode | undefined = undefined;
	private isStreaming = false;
	private isFinalStream = false;
	private aborted: string[] = [];
	private subscription: (() => void) | null = null;
	private previousValidDoc: { adfDoc?: DocNode; doc?: PMNode } | null = null;
	private logger: DebugLogger;
	private endedActionIds: string[] = [];
	private currentAction: {
		id: string;
		initialNode: PMNode | undefined;
		isCreateAction: boolean;
		type: StreamingCommand['type'];
	} | null = null;
	private insertAfterQueueLocalId: string | undefined = undefined;
	/**
	 * The traceId received in response header from AI api call
	 * This is used to trace the AI experience through the backend to frontend.
	 */
	private traceId: string | undefined = undefined;
	/**
	 * The singleInstrumentationId for tracking the AI experience
	 * This is used to correlate events across multiple stream processing passes
	 */
	private singleInstrumentationId: string | undefined = undefined;
	/**
	 * The promptVersion from action metadata
	 * This is used to track which version of the prompt was used for analytics
	 */
	private promptVersion: string | undefined = undefined;

	constructor(
		private readonly api: API,
		private readonly streamFlowPacer: StreamFlowPacer | null = null,
	) {
		this.logger = new DebugLogger(api);
		this.streamingCommands = createStreamingCommands(api);
	}

	private isActorReady(): boolean {
		try {
			const currentState = this.api.aiExperience.sharedState.currentState();
			if (!expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				// Check if actor exists and is in streaming state
				const actor = this.api.aiExperience.actions.getAiExperienceActor();
				return actor && currentState?.isStreaming === true;
			}
			return currentState?.isStreaming === true;
		} catch {
			return false;
		}
	}

	private commandToProcess(commandToProcess: QueuedStreamCommand): Promise<void> {
		const { type, actionId, chunk } = commandToProcess;

		if (type === 'initStream') {
			return this.initStreamPromise(commandToProcess.command, commandToProcess.options);
		} else if (type === 'endStream') {
			return this.endExperiencePromise(commandToProcess);
		} else {
			return this.processStreamCommandPromise(actionId, chunk);
		}
	}

	private processingQueue = false;
	private inProcessing: string[] = [];
	private queueTimeout: NodeJS.Timeout | null = null;
	// This is only used *if* we have started receiving values with a different
	// action id (new stream) and we have nothing left in the current stream (ie. it hasn't finished)
	// Really this shouldn't be hit since this would only happen on the edge when we're wrapping up the old stream
	// and starting the next early
	private CURRENT_QUEUE_TIMEOUT = 5000;
	private currentCommandTimeoutReset: (() => void) | null = null;

	private async processQueue() {
		if (this.processingQueue) {
			return;
		}
		if (this.queueTimeout) {
			clearTimeout(this.queueTimeout);
		}
		this.processingQueue = true;
		let commandsToProcess = [...this.queue].filter(
			(cmd) => cmd.actionId === this.currentAction?.id,
		);
		this.inProcessing = [...new Set(commandsToProcess.map((cmd) => cmd.actionId))];
		this.queue = this.queue.filter((cmd) => !commandsToProcess.includes(cmd));
		let currentCommand = null;

		try {
			commandsToProcess = mergeStreamCommands(commandsToProcess);
			for (const commandToProcess of commandsToProcess) {
				currentCommand = commandToProcess;
				if (this.aborted.includes(commandToProcess.actionId)) {
					// If we abort the last stream let's move onto the next stream
					if (commandToProcess.type === 'endStream') {
						this.resolvePendingQueue(true);
					}
					continue;
				}
				await this.wrapInTimeout(this.commandToProcess(commandToProcess), commandToProcess);
			}
		} catch (error) {
			if (expValEquals('platform_editor_ai_fix_insert_after_selection', 'isEnabled', true)) {
				fireCommandError({
					api: this.api,
					traceId: this.traceId,
					command: currentCommand,
					isCreateAction: this.currentAction?.isCreateAction,
					error,
				});
			} else {
				throw error;
			}
		} finally {
			// Reset the processing queue flag
			this.processingQueue = false;
			const lastCommand = commandsToProcess.pop();
			this.inProcessing = [];
			if (lastCommand?.type === 'endStream') {
				this.checkAndResolveEmptyQueue();
			}
		}
		// If commands have been added to the queue during async operations, process them now
		if (this.queue.filter((cmd) => cmd.actionId === this.currentAction?.id).length > 0) {
			this.processQueue();
		} else if (this.queue.length > 0) {
			this.queueTimeout = setTimeout(() => {
				this.logger.logError(`Current stream queue timed out while starting next`, {
					actionId: this.currentAction?.id ?? undefined,
				});
				this.resolvePendingQueue(true);
				this.processQueue();
			}, this.CURRENT_QUEUE_TIMEOUT);
		}
	}

	private onEmptyQueueResolve: (() => void) | null = null;
	public onEmptyQueue(): Promise<void> {
		const promise = new Promise<void>((resolve) => {
			this.onEmptyQueueResolve = resolve;
		});
		this.checkAndResolveEmptyQueue();
		return promise;
	}

	// 2 seconds - this is the maximum time to process a command before we abort it and continue
	// This can be longer if for example if a lot of commands have been processed, but the stream flow
	// pacer has a backlog (particularly for elements with lots of attributes / marks etc) and hasn't had
	// a chance to emit yet
	private COMMAND_TIMEOUT = 2000;

	private async wrapInTimeout(promise: Promise<void>, cmd: QueuedStreamCommand): Promise<void> {
		const { promise: wrappedPromise, resetTimeout } = createTimeoutWrapper(promise, {
			timeout: this.COMMAND_TIMEOUT,
			onTimeout: () => {
				this.logger.logError(`Stream command timed out ${cmd.type}`, {
					actionId: cmd.actionId,
					chunk: cmd.chunk,
				});
			},
			onCleanup: () => {
				this.currentCommandTimeoutReset = null;

				if (cmd.type === 'endStream') {
					this.checkAndResolveEmptyQueue();
				}
				this.resolvePendingQueue();
			},
		});

		this.currentCommandTimeoutReset = resetTimeout;
		await wrappedPromise;
	}

	/**
	 * End the AI experience
	 */
	public endStreamCommand(actionId: string): void {
		// duplicated endStreamCommand will kill accept reject screen prematurely
		if (this.endedActionIds.includes(actionId)) {
			this.logger.logPublic('End Stream Command Ignored', { actionId });
			return;
		}
		this.endedActionIds.push(actionId);
		if (this.endedActionIds.length > 10) {
			this.endedActionIds.shift();
		}

		this.logger.logPublic('End Stream Command', { actionId });
		if (this.aborted.includes(actionId) || this.isAborted) {
			this.updateAbortedCache([actionId]);
			return;
		}
		// Actor not ready, queue the command
		this.queue.push({ type: 'endStream', actionId, chunk: undefined, reason: 'endCurrentStream' });
		this.processQueue();
	}

	private endExperience() {
		// if it's isFinalStream
		// - when auto accept AI changes is on, editor-plugin-ai will end itself
		// - when auto accept AI changes is off, the accept reject screen will be shown
		// if it's not isFinalStream, the endAIExperience is used to kill a previous AI modal
		// - which can be the previous accept reject screen
		// - or the command palette screen (followed by rovo free-gen screen)
		if (!this.isFinalStream) {
			this.logger.logQueueState('End AI Experience');
			this.api.aiExperience.actions.endAIExperience();
		}
		this.isFinalStream = false;
		this.adfStreamer?.reset();
		this.streamFlowPacer?.reset();
		this.subscription?.();
	}
	private isPendingResolveQueue = false;

	private resolvePendingQueue(timeout = false) {
		if (timeout || this.isPendingResolveQueue) {
			const currentAction = this.queue.find((cmd) => cmd.type === 'initStream') ?? null;
			this.logger.logQueueState('Resolve Pending Queue', {
				actionId: currentAction?.actionId,
				reason: timeout ? 'timeout' : 'isPendingResolveQueue',
			});
			this.currentAction = currentAction
				? {
						id: currentAction.actionId,
						isCreateAction:
							(currentAction.command.type === 'appendNode' ||
								currentAction.command.type === 'appendNodeToEnd') &&
							currentAction.command.streamingType === 'create',
						initialNode: findNodeByLocalIdAndApi(
							this.api,
							currentAction.command.type === 'replaceNode' && currentAction.command.localId,
						),
						type: currentAction.command.type,
					}
				: null;
			this.isPendingResolveQueue = false;
		}
	}

	private checkAndResolveEmptyQueue() {
		if (this.queue.length === 0 && this.inProcessing.length === 0) {
			this.onEmptyQueueResolve?.();
		}
	}

	private endNonStreamble() {
		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			this.api.aiExperience.actions.dispatchAIEvent({
				type: 'complete',
				adfResponse: undefined,
			});
		} else {
			const { send } = this.api.aiExperience.actions.getAiExperienceActor();
			send({
				type: 'complete',
				markdown: '',
				adfResponse: undefined,
				cacheKey: '',
				modelInput: {},
			});
		}
	}

	private async endExperiencePromise(
		command: Extract<QueuedStreamCommand, { type: 'endStream' }>,
	): Promise<void> {
		const { actionId, reason } = command;
		this.logger.logQueueState('End Experience', { actionId, reason });
		this.isPendingResolveQueue = reason === 'endCurrentStream';
		// should not check empty queue if the end command is triggered by the initStreamCommand
		// which is used to close a previous AI modal
		// because there are more commands to process even if the queue is empty
		// and this.isPendingResolveQueue will be reset, so use a different flag here
		const shouldCheckEmptyQueue = reason === 'endCurrentStream';
		const isStreamingCommand = !(
			this.currentAction?.type && !isAStreamableCommand({ type: this.currentAction?.type })
		);

		if (this.isStreaming && isStreamingCommand) {
			await this.processStreamCommandPromise(actionId, '', true);
			if (this.currentAction?.type === 'replaceNode') {
				fireAttributeOnlyReplaceNodeAnalytics({
					api: this.api,
					initialNode: this.currentAction.initialNode,
					lastDoc: this.adfStreamer?.getLastDoc(),
				});
			}
			checkAndFireNoDocumentChangeAnalytics({
				api: this.api,
				command: this.currentCommand,
				docBeforeStreaming: this.docBeforeStreaming,
				traceId: this.traceId,
			});
		} else if (!isStreamingCommand) {
			this.isFinalStream = true;
		}
		return new Promise<void>((resolve) => {
			const endNow = () => {
				this.endExperience();
				// After we end the experience there are a number of React re-renders that occur immediately after
				// This causes the state to flick between streaming and not streaming, it's not pretty
				// but wrap this in a `requestAnimationFrame` to delay our check until this clears.
				// To fix this properly we probably need to decouple the state with React
				const checkStreaming = () => {
					const resolveAndEndStream = () => {
						resolve();
						this.logger.logQueueState('End Experience Resolved', { actionId, reason });
						this.resolvePendingQueue();
						shouldCheckEmptyQueue && this.checkAndResolveEmptyQueue();
					};
					if (!isStreamingCommand) {
						this.endNonStreamble();
						resolveAndEndStream();
						return;
					}
					if (!this.api.aiExperience?.sharedState.currentState()?.isStreaming) {
						resolveAndEndStream();
						return;
					}
					const unsubscribe = this.api.aiExperience?.sharedState.onChange(({ nextSharedState }) => {
						if (!nextSharedState.isStreaming) {
							unsubscribe();
							resolveAndEndStream();
						}
					});
				};

				setTimeout(checkStreaming, 0);
			};

			// If we're not running the pacer or its a non-streamable command, end immediately
			if (
				!this.streamFlowPacer?.isRunning() ||
				(this.currentAction?.type && !isAStreamableCommand({ type: this.currentAction?.type }))
			) {
				endNow();
			} else {
				this.streamFlowPacer.onComplete(() => {
					// Need to be careful not to end the experience too soon. We need to give the xstate and react time to process
					// the final stream events before we kill the experience. Otherwise you will see an unfinished stream in the doc.
					setTimeout(endNow, 100);
				});
			}
		});
	}

	/**
	 * Streams the AI experience
	 */
	public queueStreamCommand(actionId: string, chunk: string): void {
		this.logger.logPublic('Stream Command', { actionId, chunk });
		if (this.aborted.includes(actionId) || this.isAborted) {
			this.updateAbortedCache([actionId]);
			return;
		}
		// Actor not ready, queue the command
		this.queue.push({ type: 'streamCommand', actionId, chunk });
		if (this.isActorReady()) {
			this.processQueue();
		} else {
			// if there are multiple sequential queueStreamCommand calls
			// and we assign the return value to this.subscription and call this.subscription
			// we won't be able to unsubscribe the corresponding right one
			// and will cause the callback to be called tons of times when state changes
			const unsubscribe = this.api?.aiExperience.sharedState.onChange(() => {
				if (this.isActorReady()) {
					unsubscribe?.();
					this.processQueue();
				}
			});
		}
	}

	private hasChangedDocument(latestDoc: ReturnType<typeof makeDocumentValid>): boolean {
		if (!this.previousValidDoc && latestDoc) {
			return true;
		}

		const nodesAreNotEqual =
			!this.previousValidDoc?.doc ||
			!latestDoc?.doc ||
			!areNodesEqualIgnoreAttrs(this.previousValidDoc?.doc, latestDoc.doc);
		if (this.streamFlowPacer === null) {
			return Boolean(nodesAreNotEqual && latestDoc.doc && !isEmptyDocument(latestDoc.doc));
		}
		const latestCount = this.streamFlowPacer?.adfTextLen(latestDoc?.adfDoc);
		return Boolean(
			nodesAreNotEqual &&
			latestDoc.adfDoc &&
			this.previousValidDoc?.adfDoc &&
			latestCount.chars > 0 &&
			// Ensure we only stream changes if the stream flow pacer detects semantic changes
			!this.streamFlowPacer?.adfTextLen(this.previousValidDoc?.adfDoc).gte(latestCount),
		);
	}

	private createCallback(usePush: boolean) {
		if (usePush) {
			return (evt: EditorPluginAIEvent) => {
				const fullEvent = normaliseAIExperienceEvent(evt);
				this?.streamFlowPacer?.push(fullEvent);
			};
		}

		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			return (evt: EditorPluginAIEvent) => {
				this.api.aiExperience.actions.dispatchAIEvent(evt);
			};
		}

		// Legacy path: send to actor with padded defaults
		const { send } = this.api.aiExperience.actions.getAiExperienceActor();
		return (evt: EditorPluginAIEvent) => {
			const payload = normaliseAIExperienceEvent(evt);
			send(payload as Parameters<typeof send>[0]);
		};
	}

	private processStreamCommand(actionId: string, chunk: string, finalStream = false) {
		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			this.streamFlowPacer?.setEmit((evt: EditorPluginAIEvent) => {
				// While the stream flow pacer is doing work reset the timeout
				this.currentCommandTimeoutReset?.();
				this.api.aiExperience.actions.dispatchAIEvent(evt);
			});
		} else {
			const { send } = this.api.aiExperience.actions.getAiExperienceActor();
			this.streamFlowPacer?.setEmit((evt: Parameters<typeof send>[0]) => {
				// While the stream flow pacer is doing work reset the timeout
				this.currentCommandTimeoutReset?.();
				send(evt);
			});
		}

		// Bypass the pacer when noStream is set (e.g. pre-computed results from semantic tool calls)
		const noStream =
			expValEquals('platform_editor_ai_no_stream_skip_pacer', 'isEnabled', true) &&
			this.currentAction?.type &&
			'noStream' in this.currentAction &&
			this.currentAction.noStream;
		const callback = this.createCallback(!!this.streamFlowPacer?.push && !noStream);

		// Initialize streamer if needed
		if (!this.adfStreamer) {
			const schema = this.api.core.sharedState.currentState()?.schema;
			this.adfStreamer = schema
				? new ADFStreamer(
						schema,
						createFireADFStreamingAnalyticsEvent({
							api: this.api,
							isCreateAction: this.currentAction?.isCreateAction ?? false,
							traceId: fg('platform_editor_ai_traceid_in_analytics_events')
								? this.traceId
								: undefined,
							promptVersion: this.promptVersion,
						}),
					)
				: undefined;
		}

		if (finalStream) {
			const adfResponseBefore = makeDocumentValid(this.api, this.adfStreamer?.getLastDoc()).adfDoc;

			const schema = this.api.core.sharedState.currentState()?.schema;
			const { updatedResponse, localId } = updateInsertAfterDoc(
				adfResponseBefore,
				this.currentAction?.type,
				schema,
			);
			const adfResponse = updatedResponse;
			this.insertAfterQueueLocalId = localId;

			this.adfStreamer?.endStreamingSession({ hasProcessedFinalResponse: true });
			this.logger.logQueueState('Final Stream', { actionId, adf: adfResponse, finalStream: true });
			callback({
				type: 'complete',
				adfResponse,
			});
			return true;
		}

		// Resetting because we are going to get full ADF as chunk
		this.adfStreamer?.reset();
		this.adfStreamer?.processChunk(chunk);

		const latestDoc = makeDocumentValid(
			this.api,
			noStream ? this.adfStreamer?.getLastDoc() : this.adfStreamer?.getLastDocWithoutLastLeafNode(),
		);

		const changed = this.hasChangedDocument(latestDoc);
		this.previousValidDoc = latestDoc;

		// We compare the before/after docs because if nothing has changed then it will not trigger a react update (because the adfResponse needs to be different)
		// which means the chunkNumber will never update and the processStreamCommandPromise Promise will never resolve.
		// This cause the wrapInTimeout 1sec delay to occur before the next chunk is processed. This means we can end up in a situation
		// where we're recieving a lot of chunks, but nothing in the view is changed and everything starts taking a lot of time
		// because of the sequential 1sec failovers.
		if (changed) {
			callback({
				type: 'stream',
				adfResponse: latestDoc.adfDoc,
			});
			return true;
		}

		return false;
	}

	private processStreamCommandPromise(
		actionId: string,
		chunk: string,
		finalStream = false,
	): Promise<void> {
		this.logger.logQueueState('Streaming Command', { actionId, chunk, finalStream });
		this.isStreaming = !finalStream;
		this.isFinalStream = finalStream;
		return new Promise<void>((resolve) => {
			// do not wrap the code in setTimeout, it will pause the streaming when the tab is in background
			const chunkBefore = this.api.aiExperience?.sharedState.currentState()?.chunkNumber;
			if (!this.processStreamCommand(actionId, chunk, finalStream)) {
				// If nothing was processed then we should be able to end immediately since we will never get a chuckNumber update
				// this means the next chunk can be processed straight away.
				resolve();
				this.logger.logQueueState('Streaming Command Resolved', { actionId, chunk, finalStream });
				return;
			}

			if (chunkBefore !== this.api.aiExperience?.sharedState.currentState()?.chunkNumber) {
				resolve();
				this.logger.logQueueState('Streaming Command Resolved', { actionId, chunk, finalStream });
				return;
			}
			// if there are multiple sequential queueStreamCommand calls and one of them timeout
			// and we assign the return value to this.subscription and call this.subscription
			// we won't be able to unsubscribe the corresponding right one
			// and will cause the callback to be called tons of times when state changes
			const unsubscribe = this.api.aiExperience?.sharedState.onChange(
				({ nextSharedState, prevSharedState }) => {
					if (nextSharedState?.chunkNumber !== prevSharedState?.chunkNumber) {
						unsubscribe?.();
						resolve();
						this.logger.logQueueState('Streaming Command Resolved', {
							actionId,
							chunk,
							finalStream,
						});
					}
				},
			);
		});
	}

	/**
	 * Streams the AI experience
	 */
	public initStreamCommand(
		actionId: string,
		command: StreamingCommand,
		options?: InitOptions,
	): void {
		this.logger.logPublic('Init Command', { actionId, command });
		this.isAborted = false;

		// Track tool call captured for analytics
		if (fg('platform_editor_ai_tool_call_received_analytics')) {
			fireToolCallCapturedAnalytics({
				api: this.api,
				command,
				actionId,
				traceId: this.traceId,
			});
		}
		if (!this.currentAction) {
			this.currentAction = {
				id: actionId,
				isCreateAction:
					(command.type === 'appendNode' || command.type === 'appendNodeToEnd') &&
					command.streamingType === 'create',
				initialNode: findNodeByLocalIdAndApi(
					this.api,
					command.type === 'replaceNode' && command.localId,
				),
				type: command.type,
			};
		}
		// If we're already running end the stream and restart
		if (
			this.api.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState.isRunning
		) {
			this.queue.push({
				type: 'endStream',
				actionId,
				chunk: undefined,
				reason: 'endPreviousStream',
			});
		}
		this.queue.push({
			type: 'initStream',
			actionId,
			chunk: undefined,
			command,
			options,
		});
		this.processQueue();
	}

	private applyCommand(command: StreamingCommand) {
		const insertAfterQueue =
			this.currentCommand?.type === 'insertNodeAfter' &&
			command.type === 'insertNodeAfter' &&
			command.localId === this.currentCommand?.localId;

		this.currentCommand = command;
		this.docBeforeStreaming = getPmDoc(this.api);
		let isSuccessful = true;

		// If we have subsequent "insertAfter" commands with the same localId, instead point to the end of the
		// previous insertion. This is because the AI will "insertAfter" in a logical order, but that puts the content
		// into the document backwards.
		if (insertAfterQueue && this.insertAfterQueueLocalId) {
			// If for some reason the localId doesn't exist in the document fallback to the original stream command
			try {
				isSuccessful = this.streamingCommands[command.type]({
					...command,
					localId: this.insertAfterQueueLocalId,
				});
			} catch {
				isSuccessful = this.streamingCommands[command.type](command);
			}
		} else {
			isSuccessful = this.streamingCommands[command.type](command);
		}
		return isSuccessful;
	}

	private initStream(command: StreamingCommand, options: InitOptions) {
		const applied = this.applyCommand(command);
		// If we couldn't apply the command (ie. broken localId) add it to the abort cache so we don't apply anymore commands
		if (!applied) {
			// Never start the stream and resolve immediately
			if (this.currentAction?.id) {
				this.updateAbortedCache([this.currentAction?.id]);
			}
			return true;
		}

		this.streamFlowPacer?.reset();
		this.adfStreamer?.reset();
		// We have to set an empty callback before starting otherwise the flow pacer wont start. We need to cleanup the flow pacer
		// and make it more generic.
		this.streamFlowPacer?.setEmit(() => {});
		this.streamFlowPacer?.start();
		const isCreate =
			(command.type === 'appendNode' || command.type === 'appendNodeToEnd') &&
			command.streamingType === 'create';
		this.logger.logQueueState('Open AI Modal', { command });
		this.api.aiExperience.actions.openAIModal({
			invokedFrom: 'streamingApi',
			configItem: UNSTABLE_configItems.freeGenerateAifcConfigItem,
			withStreamingHighlight: !isCreate,
			// don't show accept reject screen for staging first-time creation streaming
			withAcceptReject: !isCreate,
			hideLoadingScreen:
				(fg('platform_editor_tmp_hide_loader') && options?.hideLoadingScreen) ?? false,
			traceId: this.traceId,
			singleInstrumentationId: this.singleInstrumentationId,
			isCreate,
			promptVersion: this.promptVersion,
		});
		if (!isAStreamableCommand(command)) {
			checkAndFireNoDocumentChangeAnalytics({
				api: this.api,
				command: this.currentCommand,
				docBeforeStreaming: this.docBeforeStreaming,
				traceId: this.traceId,
			});
		}
		return false;
	}

	private async initStreamPromise(command: StreamingCommand, options: InitOptions): Promise<void> {
		await new Promise<void>((resolve) => {
			this.logger.logQueueState(`Init Command ${command.type}`, { command });
			if (this.initStream(command, options)) {
				resolve();
				this.logger.logQueueState(`Init Command Resolved ${command.type}`, { command });
				return;
			}
			const unsubscribe = this.api.aiExperience?.sharedState.onChange(({ nextSharedState }) => {
				if (nextSharedState.aiDecorationExperiencePluginState.isRunning) {
					unsubscribe?.();
					resolve();
					this.logger.logQueueState(`Init Command Resolved ${command.type}`, { command });
				}
			});
		});
	}

	public startSession(
		sessionId: string,
		traceId?: string,
		singleInstrumentationId?: string,
		promptVersion?: string,
	): void {
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			this.traceId = traceId;
			fireSessionStartedAnalytics({ api: this.api, sessionId, traceId, promptVersion });
		}
		this.singleInstrumentationId = singleInstrumentationId;
		this.promptVersion = fg('platform_editor_ai_promptversion_in_analytics')
			? promptVersion
			: undefined;
		this.logger.logPublic('Start Session', { sessionId, singleInstrumentationId });
	}

	public queueEndSession(sessionId: string): void {
		this.logger.logPublic('Queue End Session', { sessionId });
	}

	public endSession(sessionId: string): void {
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			this.traceId = undefined;
		}
		this.singleInstrumentationId = undefined;
		this.promptVersion = undefined;
		this.currentCommand = undefined;
		this.logger.logPublic('End Session', { sessionId });
	}

	public destroy(): void {
		this.queue = [];
		this.adfStreamer = undefined;
		this.currentCommand = undefined;
		this.docBeforeStreaming = undefined;
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			this.traceId = undefined;
		}
		this.singleInstrumentationId = undefined;
		this.promptVersion = undefined;
	}

	private isAborted = false;
	/**
	 * Abort the current streaming and end the experience
	 * This is a hard abort and will clear the queue
	 * This will only restart the experience when initStreamCommand is called again
	 */
	public abort(): void {
		this.logger.logPublic('Abort');
		const currentQueue = this.queue.map((cmd) => cmd.actionId);
		this.queue = [];

		// Add new aborted action IDs to end of array
		this.updateAbortedCache([...new Set(currentQueue), ...this.inProcessing]);
		this.isAborted = true;
		this.endExperience();
		this.isPendingResolveQueue = false;
		if (this.queueTimeout) {
			clearTimeout(this.queueTimeout);
		}
		this.currentAction = null;
		this.isFinalStream = false;
		this.currentCommand = undefined;
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			this.traceId = undefined;
		}
		this.singleInstrumentationId = undefined;
	}

	private updateAbortedCache(actionIds: string[]) {
		this.aborted = [...new Set([...this.aborted, ...actionIds])];
		const maxAbortedCacheLength = 5;
		if (this.aborted.length > maxAbortedCacheLength) {
			this.aborted = this.aborted.slice(-maxAbortedCacheLength);
		}
	}
}
